import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { MainLayout } from './components/layout/MainLayout';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { useHeartbeat } from './hooks/useHeartbeat';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import FilesPage from './pages/FilesPage';
import DashboardPage from './pages/DashboardPage';
import TasksPage from './pages/TasksPage';
import TaskDetailPage from './pages/TaskDetailPage';

const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const ConversationPage = lazy(() => import('./pages/ConversationPage'));
const InviteJoinPage = lazy(() => import('./pages/InviteJoinPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const AgendaPage = lazy(() => import('./pages/AgendaPage'));
const ReleaseNotesPage = lazy(() => import('./pages/ReleaseNotesPage'));
const PhotoSearchPage = lazy(() => import('./pages/PhotoSearchPage'));
const RemindersPage = lazy(() => import('./pages/RemindersPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const RolesPage = lazy(() => import('./pages/RolesPage'));
const UserGroupsPage = lazy(() => import('./pages/UserGroupsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const BugReportsPage = lazy(() => import('./pages/BugReportsPage'));
const FeatureRequestsPage = lazy(() => import('./pages/FeatureRequestsPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const SiteTimelinePage = lazy(() => import('./pages/SiteTimelinePage'));
const DailyReportsPage = lazy(() => import('./pages/DailyReportsPage'));
const DailyReportFormPage = lazy(() => import('./pages/DailyReportFormPage'));
const PunchListPage = lazy(() => import('./pages/PunchListPage'));
const AttendancePage = lazy(() => import('./pages/AttendancePage'));
const AttendanceQrCodesPage = lazy(() => import('./pages/AttendanceQrCodesPage'));
const InspectionTemplatesPage = lazy(() => import('./pages/InspectionTemplatesPage'));
const InspectionFormBuilderPage = lazy(() => import('./pages/InspectionFormBuilderPage'));
const InspectionSubmissionsPage = lazy(() => import('./pages/InspectionSubmissionsPage'));
const InspectionFillPage = lazy(() => import('./pages/InspectionFillPage'));
const StockHubPage = lazy(() => import('./pages/StockHubPage'));
const StockItemDetailPage = lazy(() => import('./pages/StockItemDetailPage'));
const SubcontractDetailPage = lazy(() => import('./pages/SubcontractDetailPage'));
const ProgressEntriesPage = lazy(() => import('./pages/ProgressEntriesPage'));
const BillingsPage = lazy(() => import('./pages/BillingsPage'));
const GreenBookPage = lazy(() => import('./pages/GreenBookPage'));
const SubcontractHubPage = lazy(() => import('./pages/SubcontractHubPage'));
const DrawingsPage = lazy(() => import('./pages/DrawingsPage'));
const DrawingDetailPage = lazy(() => import('./pages/DrawingDetailPage'));
const DrawingComparePage = lazy(() => import('./pages/DrawingComparePage'));
const SubcontractorInvitePage = lazy(() => import('./pages/SubcontractorInvitePage'));
const SubcontractorTasksPage = lazy(() => import('./pages/SubcontractorTasksPage'));
const CorrespondencePage = lazy(() => import('./pages/CorrespondencePage'));
const RfiDetailPage = lazy(() => import('./pages/RfiDetailPage'));
const TransmittalDetailPage = lazy(() => import('./pages/TransmittalDetailPage'));
const SubmittalDetailPage = lazy(() => import('./pages/SubmittalDetailPage'));
const InstructionsPage = lazy(() => import('./pages/InstructionsPage'));
const InstructionDetailPage = lazy(() => import('./pages/InstructionDetailPage'));
const OrientationProgramsPage = lazy(() => import('./pages/OrientationProgramsPage'));
const OrientationSessionsPage = lazy(() => import('./pages/OrientationSessionsPage'));
const OrientationSessionDetailPage = lazy(() => import('./pages/OrientationSessionDetailPage'));
const OrientationExpiringPage = lazy(() => import('./pages/OrientationExpiringPage'));
const EquipmentPage = lazy(() => import('./pages/EquipmentPage'));
const EquipmentDetailPage = lazy(() => import('./pages/EquipmentDetailPage'));
const EquipmentTimePage = lazy(() => import('./pages/EquipmentTimePage'));
const SafetyDashboardPage = lazy(() => import('./pages/SafetyDashboardPage'));
const SafetyIncidentsPage = lazy(() => import('./pages/SafetyIncidentsPage'));
const SafetyIncidentDetailPage = lazy(() => import('./pages/SafetyIncidentDetailPage'));
const RiskAssessmentPage = lazy(() => import('./pages/RiskAssessmentPage'));
const SiteTourPage = lazy(() => import('./pages/SiteTourPage'));
const SiteTourComparePage = lazy(() => import('./pages/SiteTourComparePage'));
const BimModelsPage = lazy(() => import('./pages/BimModelsPage'));
// BIM viewer ağır bağımlılıkları (web-ifc wasm + three) izole chunk'ta tutar
const BimViewerPage = lazy(() => import('./pages/BimViewerPage'));
const ChecksPage = lazy(() => import('./pages/ChecksPage'));
const ExpensesPage = lazy(() => import('./pages/ExpensesPage'));
const CashAccountsPage = lazy(() => import('./pages/CashAccountsPage'));
const ConcretePage = lazy(() => import('./pages/ConcretePage'));
const CashFlowPage = lazy(() => import('./pages/CashFlowPage'));
const WorkerDocumentsPage = lazy(() => import('./pages/WorkerDocumentsPage'));
const GuaranteesPage = lazy(() => import('./pages/GuaranteesPage'));
const LeavesPage = lazy(() => import('./pages/LeavesPage'));
const CopqPage = lazy(() => import('./pages/CopqPage'));
const EvmPage = lazy(() => import('./pages/EvmPage'));
const AiAssistantPage = lazy(() => import('./pages/AiAssistantPage'));
const SgkLaborPage = lazy(() => import('./pages/SgkLaborPage'));
const ToolboxTalksPage = lazy(() => import('./pages/ToolboxTalksPage'));
const UnitsPage = lazy(() => import('./pages/UnitsPage'));
const UnitSalesPage = lazy(() => import('./pages/UnitSalesPage'));
const UnitSaleDetailPage = lazy(() => import('./pages/UnitSaleDetailPage'));
const BidRequestsPage = lazy(() => import('./pages/BidRequestsPage'));
const BidRequestDetailPage = lazy(() => import('./pages/BidRequestDetailPage'));
const BidInvitePage = lazy(() => import('./pages/BidInvitePage'));
const SalePortalPage = lazy(() => import('./pages/SalePortalPage'));
const KioskPage = lazy(() => import('./pages/KioskPage'));
const MeetingsPage = lazy(() => import('./pages/MeetingsPage'));
const MeetingDetailPage = lazy(() => import('./pages/MeetingDetailPage'));
const ErpMappingsPage = lazy(() => import('./pages/ErpMappingsPage'));
const ReportBuilderPage = lazy(() => import('./pages/ReportBuilderPage'));
const CashCalendarPage = lazy(() => import('./pages/CashCalendarPage'));
const MaliTakvimPage = lazy(() => import('./pages/MaliTakvimPage'));
const BookingPage = lazy(() => import('./pages/BookingPage'));
const SantiyeMPage = lazy(() => import('./pages/SantiyeMPage'));
const CompliancePage = lazy(() => import('./pages/CompliancePage'));
const DeliveryPage = lazy(() => import('./pages/DeliveryPage'));
const MaliHaberlerPage = lazy(() => import('./pages/MaliHaberlerPage'));
const SchedulePage = lazy(() => import('./pages/SchedulePage'));
const WorkPermitsPage = lazy(() => import('./pages/WorkPermitsPage'));
const SkillsPage = lazy(() => import('./pages/SkillsPage'));
const EstimatesPage = lazy(() => import('./pages/EstimatesPage'));
const EstimateDetailPage = lazy(() => import('./pages/EstimateDetailPage'));
const BbsObservationsPage = lazy(() => import('./pages/BbsObservationsPage'));
const SalesLeadsPage = lazy(() => import('./pages/SalesLeadsPage'));
const EmployerInvitePage = lazy(() => import('./pages/EmployerInvitePage'));
const EmployerPortalPage = lazy(() => import('./pages/EmployerPortalPage'));

function App() {
  useHeartbeat();

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Dış kullanıcı (taşeron) public akışı — auth layout dışı, kendi ability token'ı ile çalışır */}
          <Route path="/subcontractor/invite/:token" element={<SubcontractorInvitePage />} />
          <Route path="/subcontractor/tasks" element={<SubcontractorTasksPage />} />
          {/* Public teklif daveti — auth'siz, kendi mini axios'u ile çalışır */}
          <Route path="/bid-invite/:token" element={<BidInvitePage />} />
          {/* Public alıcı portalı — auth'siz, kendi mini axios'u ile çalışır */}
          <Route path="/sale-portal/:token" element={<SalePortalPage />} />
          {/* A5: public kiosk tablet ekranı — auth'siz, kiosk oturum tokeni ile çalışır */}
          <Route path="/kiosk" element={<KioskPage />} />
          {/* C8: public randevu sayfası — auth'siz, kendi mini axios'u ile çalışır */}
          <Route path="/book/:slug" element={<BookingPage />} />
          {/* İşveren (owner mode): public davet + salt-okunur portal — ayrı ability token */}
          <Route path="/employer/invite/:token" element={<EmployerInvitePage />} />
          <Route path="/employer" element={<EmployerPortalPage />} />
          <Route element={<MainLayout />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks"
              element={
                <ProtectedRoute>
                  <TasksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks/:id"
              element={
                <ProtectedRoute>
                  <TaskDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/files"
              element={
                <ProtectedRoute>
                  <FilesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <MessagesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages/:id"
              element={
                <ProtectedRoute>
                  <ConversationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages/join/:token"
              element={
                <ProtectedRoute>
                  <InviteJoinPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <CalendarPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agenda"
              element={
                <ProtectedRoute>
                  <AgendaPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/release-notes"
              element={
                <ProtectedRoute>
                  <ReleaseNotesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/photos-search"
              element={
                <ProtectedRoute>
                  <PhotoSearchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reminders"
              element={
                <ProtectedRoute>
                  <RemindersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <ProtectedRoute>
                  <ProjectsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:id/site-timeline"
              element={
                <ProtectedRoute>
                  <SiteTimelinePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:id/punch-list"
              element={
                <ProtectedRoute>
                  <PunchListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:id/attendance"
              element={
                <ProtectedRoute>
                  <AttendancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:id/attendance/qr-codes"
              element={
                <ProtectedRoute>
                  <AttendanceQrCodesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inspections/templates"
              element={
                <ProtectedRoute>
                  <InspectionTemplatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inspections/templates/:id/builder"
              element={
                <ProtectedRoute>
                  <InspectionFormBuilderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:id/inspections"
              element={
                <ProtectedRoute>
                  <InspectionSubmissionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:id/inspections/fill/:templateId"
              element={
                <ProtectedRoute>
                  <InspectionFillPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/daily-reports"
              element={
                <ProtectedRoute>
                  <DailyReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/daily-reports/new"
              element={
                <ProtectedRoute>
                  <DailyReportFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/daily-reports/:id/edit"
              element={
                <ProtectedRoute>
                  <DailyReportFormPage />
                </ProtectedRoute>
              }
            />
            {/* M3: Stok & Malzeme birleşik sayfası; eski liste path'leri exact-match redirect (detay route'u /stock/:id değişmez) */}
            <Route
              path="/stock-hub"
              element={
                <ProtectedRoute>
                  <StockHubPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stock"
              element={<Navigate to="/stock-hub?tab=envanter" replace />}
            />
            <Route
              path="/stock/:id"
              element={
                <ProtectedRoute>
                  <StockItemDetailPage />
                </ProtectedRoute>
              }
            />
            {/* M2: Taşeron & Hakediş birleşik sayfası; eski liste path'leri exact-match redirect (detay route'ları değişmez) */}
            <Route
              path="/subcontract-hub"
              element={
                <ProtectedRoute>
                  <SubcontractHubPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subcontractors"
              element={<Navigate to="/subcontract-hub?tab=sozlesmeler" replace />}
            />
            <Route
              path="/subcontractors/:id"
              element={
                <ProtectedRoute>
                  <SubcontractDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subcontracts/:id/progress"
              element={
                <ProtectedRoute>
                  <ProgressEntriesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subcontracts/:id/billings"
              element={
                <ProtectedRoute>
                  <BillingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subcontracts/:id/green-book"
              element={
                <ProtectedRoute>
                  <GreenBookPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bids"
              element={
                <ProtectedRoute>
                  <BidRequestsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bids/:id"
              element={
                <ProtectedRoute>
                  <BidRequestDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/price-indices"
              element={<Navigate to="/subcontract-hub?tab=endeksler" replace />}
            />
            <Route
              path="/public-billing-deadlines"
              element={<Navigate to="/subcontract-hub?tab=sureler" replace />}
            />
            {/* M4: Sesli Notlar Şantiye Defteri sekmesine taşındı; eski path exact-match redirect */}
            <Route
              path="/voice-notes"
              element={<Navigate to="/daily-reports?tab=sesli-notlar" replace />}
            />
            <Route
              path="/drawings"
              element={
                <ProtectedRoute>
                  <DrawingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/drawings/:id"
              element={
                <ProtectedRoute>
                  <DrawingDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/drawings/:id/compare"
              element={
                <ProtectedRoute>
                  <DrawingComparePage />
                </ProtectedRoute>
              }
            />
            {/* M1: Resmi Yazışma birleşik sayfası; eski liste path'leri exact-match redirect (detay route'ları değişmez) */}
            <Route
              path="/correspondence"
              element={
                <ProtectedRoute>
                  <CorrespondencePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rfis"
              element={<Navigate to="/correspondence?tab=rfi" replace />}
            />
            <Route
              path="/rfis/:id"
              element={
                <ProtectedRoute>
                  <RfiDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/transmittals"
              element={<Navigate to="/correspondence?tab=transmittal" replace />}
            />
            <Route
              path="/transmittals/:id"
              element={
                <ProtectedRoute>
                  <TransmittalDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/submittals"
              element={<Navigate to="/correspondence?tab=submittal" replace />}
            />
            <Route
              path="/submittals/:id"
              element={
                <ProtectedRoute>
                  <SubmittalDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructions"
              element={
                <ProtectedRoute>
                  <InstructionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructions/:id"
              element={
                <ProtectedRoute>
                  <InstructionDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orientations"
              element={
                <ProtectedRoute>
                  <OrientationSessionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orientations/programs"
              element={
                <ProtectedRoute>
                  <OrientationProgramsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orientations/sessions/:id"
              element={
                <ProtectedRoute>
                  <OrientationSessionDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orientations/expiring"
              element={
                <ProtectedRoute>
                  <OrientationExpiringPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/equipment"
              element={
                <ProtectedRoute>
                  <EquipmentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/equipment/:id"
              element={
                <ProtectedRoute>
                  <EquipmentDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/equipment-time"
              element={
                <ProtectedRoute>
                  <EquipmentTimePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/safety"
              element={
                <ProtectedRoute>
                  <SafetyDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/safety/incidents"
              element={
                <ProtectedRoute>
                  <SafetyIncidentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/safety/incidents/:id"
              element={
                <ProtectedRoute>
                  <SafetyIncidentDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/safety/risk-assessments"
              element={
                <ProtectedRoute>
                  <RiskAssessmentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tours"
              element={
                <ProtectedRoute>
                  <SiteTourPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tours/compare"
              element={
                <ProtectedRoute>
                  <SiteTourComparePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bim"
              element={
                <ProtectedRoute>
                  <BimModelsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bim/:id"
              element={
                <ProtectedRoute>
                  <BimViewerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checks"
              element={
                <ProtectedRoute>
                  <ChecksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses"
              element={
                <ProtectedRoute>
                  <ExpensesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cash"
              element={
                <ProtectedRoute>
                  <CashAccountsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/concrete"
              element={
                <ProtectedRoute>
                  <ConcretePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/meetings"
              element={
                <ProtectedRoute>
                  <MeetingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/meetings/:id"
              element={
                <ProtectedRoute>
                  <MeetingDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/erp-mappings"
              element={
                <ProtectedRoute requireAdmin>
                  <ErpMappingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cash-flow"
              element={
                <ProtectedRoute>
                  <CashFlowPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cash-calendar"
              element={
                <ProtectedRoute>
                  <CashCalendarPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mali-takvim"
              element={
                <ProtectedRoute>
                  <MaliTakvimPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/report-builder"
              element={
                <ProtectedRoute>
                  <ReportBuilderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/worker-documents"
              element={
                <ProtectedRoute>
                  <WorkerDocumentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/guarantees"
              element={
                <ProtectedRoute>
                  <GuaranteesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leaves"
              element={
                <ProtectedRoute>
                  <LeavesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/copq"
              element={
                <ProtectedRoute>
                  <CopqPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/evm"
              element={
                <ProtectedRoute>
                  <EvmPage />
                </ProtectedRoute>
              }
            />
            {/* M5: Lookahead Görevler görünümüne taşındı; eski path exact-match redirect */}
            <Route
              path="/lookahead"
              element={<Navigate to="/tasks?view=lookahead" replace />}
            />
            <Route
              path="/material-requests"
              element={<Navigate to="/stock-hub?tab=talepler" replace />}
            />
            <Route
              path="/ai-assistant"
              element={
                <ProtectedRoute>
                  <AiAssistantPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-orders"
              element={<Navigate to="/subcontract-hub?tab=co" replace />}
            />
            <Route
              path="/sgk-labor"
              element={
                <ProtectedRoute>
                  <SgkLaborPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/santiye-m"
              element={
                <ProtectedRoute>
                  <SantiyeMPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/compliance"
              element={
                <ProtectedRoute>
                  <CompliancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/delivery"
              element={
                <ProtectedRoute>
                  <DeliveryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mali-haberler"
              element={
                <ProtectedRoute>
                  <MaliHaberlerPage />
                </ProtectedRoute>
              }
            />
            {/* Dalga 3 (2026-08-07): iş programı, iş izinleri, beceri matrisi, keşif, BBS, satış CRM */}
            <Route
              path="/schedule"
              element={
                <ProtectedRoute>
                  <SchedulePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/work-permits"
              element={
                <ProtectedRoute>
                  <WorkPermitsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/skills"
              element={
                <ProtectedRoute>
                  <SkillsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/estimates"
              element={
                <ProtectedRoute>
                  <EstimatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/estimates/:id"
              element={
                <ProtectedRoute>
                  <EstimateDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bbs-observations"
              element={
                <ProtectedRoute>
                  <BbsObservationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales-leads"
              element={
                <ProtectedRoute>
                  <SalesLeadsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/toolbox-talks"
              element={
                <ProtectedRoute>
                  <ToolboxTalksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/units"
              element={
                <ProtectedRoute>
                  <UnitsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/unit-sales"
              element={
                <ProtectedRoute>
                  <UnitSalesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/unit-sales/:id"
              element={
                <ProtectedRoute>
                  <UnitSaleDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute requireAdmin>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/roles"
              element={
                <ProtectedRoute requireAdmin>
                  <RolesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-groups"
              element={
                <ProtectedRoute requireAdmin>
                  <UserGroupsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute requireAdmin>
                  <ReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bug-reports"
              element={
                <ProtectedRoute requireAdmin>
                  <BugReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/feature-requests"
              element={
                <ProtectedRoute requireAdmin>
                  <FeatureRequestsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/support"
              element={
                <ProtectedRoute>
                  <SupportPage />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
